/**
 * Loads a board image and returns it as a PNG blob for the system clipboard.
 *
 * The clipboard API accepts only `image/png` for pictures (`image/jpeg` is rejected — measured
 * 2026-10-28), so any other type is re-encoded through a canvas. `fetch` sends the session
 * cookies for same-origin sources (open2done serves images behind its login); it does not reject
 * on HTTP errors, so the status is checked explicitly — an error page would otherwise reach the
 * clipboard as a non-PNG and the browser would keep the PREVIOUS clipboard content (measured).
 *
 * @param source The image object's `src` or `dataUrl`.
 * @param isCurrent False once a newer copy has started; checked after loading and again after
 *   encoding, right before the promise settles, so an overtaken copy never lands.
 */
export async function pngBlobForClipboard(source: string, isCurrent: () => boolean): Promise<Blob> {
  const response = await fetch(source, { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`image request failed with HTTP ${response.status}`);
  const loaded = await response.blob();
  assertCurrent(isCurrent);
  const png = loaded.type === 'image/png' ? loaded : await reencodeAsPng(loaded);
  assertCurrent(isCurrent);
  return png;
}

/** Throws when a newer copy has started, so this one is dropped instead of written. */
function assertCurrent(isCurrent: () => boolean): void {
  if (!isCurrent()) throw new Error('superseded by a newer copy');
}

/** Draws any decodable image at its natural size and encodes it as PNG. */
async function reencodeAsPng(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d canvas context');
    context.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('PNG encoding failed'))), 'image/png');
    });
  } finally {
    bitmap.close();
  }
}
